import { ClipboardEvent, ReactNode, useState } from 'react';
import { StarRating } from '@components/elements';
import DATE_FORMATS from '@constants/dateFormat';
import { Comment } from '@th-types/workerProfile.type';
import * as workerApi from '@worker/services/worker/api';
import { RatingReply } from '@worker/types/worker-profile-types.type';
import { format } from 'date-fns';
import { Badge, Button, Col, Form, Image, Row, Stack } from 'react-bootstrap';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IRatingVisibility,
  RATINGS_CURRENT_VERSION,
} from '@th-types/rating.type';
import '../styles.scss';

const MAX_REPLY_LENGTH = 300;

interface RatingCommentProps {
  comment: Comment;
  onReplySubmit?: (commentId: number, reply: string) => void;
  existingReply?: RatingReply;
  loadingReplies?: boolean;
}

function renderLegacyComment(comment: Comment): ReactNode {
  return <p>{comment.comments}</p>;
}

function renderComment(comment: Comment): ReactNode {
  return (
    <>
      <Stack>
        <b>What did this worker do really well?</b>
        <p>{comment.comments || 'N/A'}</p>
      </Stack>
      <Stack>
        <span>
          <b>What can they improve on?</b>
          <small style={{ color: 'var(--silver)' }}>
            {comment.improvementComments &&
            comment.visibility === IRatingVisibility.PRIVATE
              ? ' (This is visible only for you)'
              : ''}
          </small>
        </span>
        <p>{comment.improvementComments || 'N/A'}</p>
      </Stack>
    </>
  );
}

function RatingComment({
  comment,
  onReplySubmit,
  existingReply,
  loadingReplies = false,
}: RatingCommentProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const queryClient = useQueryClient();

  const replyMutation = useMutation({
    mutationFn: (text: string) => workerApi.replyToComment(comment.id, text),
    onSuccess: () => {
      // Invalidate and refetch replies
      queryClient.invalidateQueries({ queryKey: ['review-replies'] });
      setShowReplyForm(false);
      setReplyText('');
      if (onReplySubmit) {
        onReplySubmit(comment.id, replyText);
      }
    },
    onError: (error) => {
      console.error('Failed to submit reply:', error);
    },
  });

  const handleReplyClick = () => {
    setShowReplyForm(true);
  };

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;
    replyMutation.mutate(replyText.trim());
  };

  const handleCancelReply = () => {
    setShowReplyForm(false);
    setReplyText('');
  };

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const { value, selectionStart, selectionEnd } = e.currentTarget;

    const textBefore = value.substring(0, selectionStart ?? 0);
    const textAfter = value.substring(selectionEnd ?? 0);

    const availableSpace =
      MAX_REPLY_LENGTH - (textBefore.length + textAfter.length);

    if (availableSpace > 0) {
      const textToInsert = pastedText.substring(0, availableSpace);
      const newText = `${textBefore}${textToInsert}${textAfter}`;
      setReplyText(newText);
    }
  };

  const hasExistingReply = !!existingReply;
  const canReply =
    comment.canReply !== false &&
    !hasExistingReply &&
    !comment.reply &&
    comment.type === 'rating';

  return comment ? (
    <Stack>
      <Stack direction="horizontal" className="justify-content-between">
        <b className="review-date">
          {format(new Date(comment.dateCreated), DATE_FORMATS.DATE_FORMAT)}
        </b>
        {comment.noCallNoShowed && (
          <Badge className="no-call-no-show">No Call/No Show</Badge>
        )}
        {comment.canceledUnexcused && (
          <Badge className="no-call-no-show">Canceled within 24 hours</Badge>
        )}
        {comment.ratingVersion === RATINGS_CURRENT_VERSION &&
          !comment.noCallNoShowed &&
          !comment.canceledUnexcused && (
            <StarRating value={comment.avgRating || 0} color="yellow" />
          )}
      </Stack>
      <Row className="mb-2 align-items-center">
        <Col xs={6} md={3} className="fw-bold">
          Company
        </Col>
        <Col>
          <Image
            width={30}
            height={30}
            src={comment.profileImageUrl}
            roundedCircle
          />
          <span className="ms-2">{comment.companyName}</span>
        </Col>
      </Row>
      {comment.ratingVersion === RATINGS_CURRENT_VERSION && (
        <Row className="mb-2 align-items-center">
          <Col xs={6} md={3} className="fw-bold">
            <b>Position</b>
          </Col>
          <Col>
            <span>{comment.position}</span>
          </Col>
        </Row>
      )}
      {!comment.noCallNoShowed &&
        !comment.canceledUnexcused &&
        comment.ratingVersion === RATINGS_CURRENT_VERSION && (
          <Row className="mb-2 align-items-center">
            <Col xs={6} md={3} className="fw-bold">
              <b>Days Worked</b>
            </Col>
            <Col>{Math.floor(comment.workedDays)}</Col>
          </Row>
        )}
      <hr className="divider" />
      {comment.ratingVersion !== 2
        ? renderLegacyComment(comment)
        : renderComment(comment)}

      {canReply && !showReplyForm && (
        <div className="mt-3 d-flex justify-content-end">
          <Button variant="primary" size="sm" onClick={handleReplyClick}>
            REPLY
          </Button>
        </div>
      )}

      {showReplyForm && (
        <div className="mt-3">
          <Form.Group>
            <Form.Label>
              <strong>Your reply</strong>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onPaste={handlePaste}
              placeholder="Enter text here. This response will be public on your profile, please be professional."
              maxLength={MAX_REPLY_LENGTH}
            />
            <Form.Text className="text-muted">
              {replyText.length}/{MAX_REPLY_LENGTH}
            </Form.Text>
          </Form.Group>
          <div className="mt-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmitReply}
              disabled={!replyText.trim() || replyMutation.isPending}
              className="me-2"
            >
              {replyMutation.isPending ? 'SUBMITTING...' : 'SUBMIT REPLY'}
            </Button>
            <Button
              variant="light"
              size="sm"
              onClick={handleCancelReply}
              disabled={replyMutation.isPending}
            >
              CANCEL
            </Button>
          </div>
        </div>
      )}

      {/* Display Existing Replies */}
      {loadingReplies && (
        <div className="mt-3">
          <small className="text-muted">Loading replies...</small>
        </div>
      )}
      {existingReply && (
        <div className="mt-3 ms-3">
          <div>
            <strong>Your reply</strong>
          </div>
          {!existingReply.verified && (
            <small className="text-muted mb-1">(Pending approval)</small>
          )}
          <p className="mb-0">{existingReply.comments}</p>
        </div>
      )}

      {/* Display Reply */}
      {comment.reply && (
        <div className="mt-3 ms-3">
          <div>
            <strong>Your reply</strong>
          </div>
          <small className="text-muted mb-1">(Pending approval)</small>
          <p className="mb-0">{comment.reply}</p>
        </div>
      )}
    </Stack>
  ) : null;
}

export default RatingComment;
